/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useCallback, useMemo } from "react"
import { Card } from "@duxton-web/ui"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { SortableItemList } from "components/common"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { Item } from "types/models/menu"
import useInventoryContext from "hooks/useInventoryContext"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { MENU_DETAIL } from "constants/routes"
import { fullPathWithSearchParams } from "common/utils/uri"
import { Config } from "constants/config"
import { isItemWithoutPhotoMode } from "utils/menu_helper"
import commonStyles from "styles/common.modules.css"
import styles from "./MenuOverviewItemsResult.modules.css"
import { MenuItemRow } from "./MenuItemRow"
import { MenuItemRowLoading } from "./MenuItemRowLoading"

const ITEM_HEIGHT = 134 // Fixed height per item

interface IProps {
  hasAction: boolean
  routePrefix?: string
}

export const MenuOverviewItemsResult: FC<IProps> = ({
  hasAction,
  routePrefix,
}: IProps) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const {
    merchantID,
    overview: { search: searchValue, sellingTime },
    drawer: { showItemSelection },
    isMartStore,
    isMartDepartmentStructure,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
    searchResults,
    searchItemLoading,
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  const { disableAvailability } = useInventoryContext()

  const textMap = useTranslationsMap({
    noSearchResult: "common.noSearchResult",
  })

  const filteredItems: Item[] = useMemo(
    () =>
      (searchResults || []).filter(item => {
        // Filter by photo coverage if enabled
        if (
          Config.enableItemPhotoCoverage &&
          isItemWithoutPhotoMode(itemFilter)
        ) {
          if (item.imageURL || (item.imageURLs && item.imageURLs.length > 0)) {
            return false
          }
        }

        // Filter by selected selling time
        if (sellingTime) {
          return item.sellingTimeID === sellingTime.sellingTimeID
        }

        return true
      }),
    [searchResults, itemFilter, sellingTime],
  )

  const showItemDrawer = (item: Item, sender: string = "") => {
    const clickEditTimestamp = new Date().valueOf()

    const nextPath = `${MENU_DETAIL({
      isMartStore,
      merchantID: menuGroupID || merchantID,
      tabKey: "menuOverview",
      itemID: item.itemID,
      customRoutePrefix: routePrefix,
    })}`

    const destination = fullPathWithSearchParams({
      nextPath,
    })
    history.replace({
      ...destination,
    })

    dispatch(
      actionCreator.menu.toggleDrawer({
        item,
        showItem: true,
        sourceShowItem: EventCategory.menuOverviewModifyItem,
        showUploadPhoto: sender === "addButton",
      }),
    )

    let extraLabels = {}
    if (TroyWebTracker.isMenuGroup) {
      extraLabels = {
        ...extraLabels,
        CLICK_EDIT_TIMESTAMP: clickEditTimestamp,
        EDIT_SHOWN_TIMESTAMP: new Date().valueOf(),
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickEditItem,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ITEM_ID: item.itemID,
        ...extraLabels,
        TRAFFIC_MEDIUM: sender === "addButton" ? "MISSING_PHOTO" : "",
        IS_SEARCH_RESULT: true,
      },
    })
  }

  const renderRowContent = (item: Item) => {
    /**
     * mart category structure: category name
     * mart department structure: department name > sub department name
     */
    const itemParentInfo = isMartDepartmentStructure
      ? `${item.parentItemClassName} > ${item.itemClassName}`
      : `${item.categoryName}`

    return (
      <>
        <div className={styles.categoryLabel}>{itemParentInfo}</div>
        <MenuItemRow
          onClickAddPhoto={() => {
            showItemDrawer(item, "addButton")
          }}
          item={item}
          showSelection={showItemSelection}
          showDropdown={hasAction && !disableAvailability}
          highlightString={searchValue}
        />
      </>
    )
  }

  const renderContent = () => {
    // Show loading skeleton first
    if (searchItemLoading) {
      return <MenuItemRowLoading showCategoryLabel />
    }

    // Show items if we have results
    if (filteredItems.length > 0) {
      return (
        <SortableItemList
          items={filteredItems}
          renderRowContent={renderRowContent}
          itemSize={ITEM_HEIGHT}
          useDragHandle
          onRowClick={(_, item: Item) => showItemDrawer(item)}
          draggable={false}
        />
      )
    }

    // Show empty state only when not loading
    return <div className={styles.emptyState}>{textMap.noSearchResult}</div>
  }

  const resetFilterAndSelection = useCallback(() => {
    // reset checkbox selection
    dispatch(
      actionCreator.menu.toggleDrawer({
        showItemSelection: false,
        itemSelections: {},
      }),
    )
    // Reset selling time
    dispatch(actionCreator.menu.selectSellingTime(undefined))
  }, [dispatch])

  // Case 1: On mount
  useEffect(() => {
    resetFilterAndSelection()

    // Case 3: On unmount
    return resetFilterAndSelection
  }, [dispatch, resetFilterAndSelection])

  // Case 2: When searchItemLoading is true
  useEffect(() => {
    if (searchItemLoading) {
      resetFilterAndSelection()
    }
  }, [resetFilterAndSelection, searchItemLoading])

  return (
    <Card
      className={commonStyles.menuTableCard}
      title={useTranslation("menu.headings.items")}
    >
      {renderContent()}
    </Card>
  )
}
